import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAppData } from "../context/AppDataContext";
import {
  IoHome,
  IoHomeOutline,
  IoSearch,
  IoSearchOutline,
  IoAdd,
  IoChatbubbleEllipses,
  IoChatbubbleEllipsesOutline,
  IoPerson,
  IoPersonOutline,
  IoLogIn,
  IoLogInOutline,
  IoPersonAdd,
  IoPersonAddOutline,
} from "react-icons/io5";

const MobileBottomNavbar = () => {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const { unreadMessages } = useAppData();
  const location = useLocation();

  const navItems = isAuthenticated
    ? [
        {
          to: "/",
          label: t("navbar.home", "Home"),
          icon: <IoHomeOutline size={22} />,
          activeIcon: <IoHome size={22} />,
          isActive: (pathname) => pathname === "/",
        },
        {
          to: "/search",
          label: t("navbar.shop", "Shop"),
          icon: <IoSearchOutline size={22} />,
          activeIcon: <IoSearch size={22} />,
          isActive: (pathname) =>
            pathname.startsWith("/search") || pathname.startsWith("/category"),
        },
        {
          to: "/products/add",
          label: t("navbar.sell", "Sell"),
          icon: <IoAdd size={27} />,
          activeIcon: <IoAdd size={27} strokeWidth={24} />,
          isActive: (pathname) => pathname === "/products/add",
        },
        {
          to: "/chat/all",
          label: t("navbar.myMessages", "Messages"),
          icon: <IoChatbubbleEllipsesOutline size={22} />,
          activeIcon: <IoChatbubbleEllipses size={22} />,
          badge: unreadMessages,
          isActive: (pathname) => pathname.startsWith("/chat"),
        },
        {
          to: user?.is_staff || user?.is_superuser ? "/admin" : "/profile",
          label: t("navbar.account", "Account"),
          icon: <IoPersonOutline size={22} />,
          activeIcon: <IoPerson size={22} />,
          isActive: (pathname) =>
            pathname.startsWith("/profile") || pathname.startsWith("/admin"),
        },
      ]
    : [
        {
          to: "/",
          label: t("navbar.home", "Home"),
          icon: <IoHomeOutline size={22} />,
          activeIcon: <IoHome size={22} />,
          isActive: (pathname) => pathname === "/",
        },
        {
          to: "/search",
          label: t("navbar.shop", "Shop"),
          icon: <IoSearchOutline size={22} />,
          activeIcon: <IoSearch size={22} />,
          isActive: (pathname) =>
            pathname.startsWith("/search") || pathname.startsWith("/category"),
        },
        {
          to: "/products/add",
          label: t("navbar.sell", "Sell"),
          icon: <IoAdd size={27} />,
          activeIcon: <IoAdd size={27} strokeWidth={24} />,
          isActive: (pathname) => pathname === "/products/add",
        },
        {
          to: "/login",
          label: t("authPage.login", "Login"),
          icon: <IoLogInOutline size={23} />,
          activeIcon: <IoLogIn size={23} />,
          isActive: (pathname) =>
            pathname === "/login" && !location.search.includes("signup"),
        },
        {
          to: "/login?mode=signup",
          label: t("authPage.signUp", "Sign Up"),
          icon: <IoPersonAddOutline size={22} />,
          activeIcon: <IoPersonAdd size={22} />,
          isActive: (pathname) =>
            pathname === "/login" && location.search.includes("signup"),
        },
      ];

  return (
    <nav
      aria-label="Mobile Navigation"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000] px-4 flex justify-center pb-[max(env(safe-area-inset-bottom),14px)] pointer-events-none"
    >
      {/* Translucent Frosted Glassmorphism Dock */}
      <div className="pointer-events-auto bg-white/75 dark:bg-[#121214]/80 backdrop-blur-2xl border border-white/50 dark:border-white/10 shadow-[0_12px_40px_rgba(0,0,0,0.12)] dark:shadow-[0_16px_48px_rgba(0,0,0,0.6)] rounded-full px-2 py-1.5 flex items-center justify-between w-full max-w-[340px]">
        {navItems.map((item, index) => {
          const active = item.isActive(location.pathname);

          return (
            <NavLink
              key={`${item.to}-${index}`}
              to={item.to}
              aria-label={item.label}
              title={item.label}
              className="group outline-none select-none no-underline"
            >
              {/* Static Solid Circular Button */}
              <div
                className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 ${
                  active
                    ? "bg-gradient-to-tr from-[#ff5e00] to-[#ff9f0a] text-white shadow-[0_4px_16px_rgba(255,94,0,0.42)] scale-105"
                    : "text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
                }`}
              >
                {/* Dynamic State Icon: Solid filled when active, refined outline when inactive */}
                <span className="flex items-center justify-center shrink-0 transition-transform duration-150">
                  {active ? item.activeIcon : item.icon}
                </span>

                {/* Notification / Unread Badge */}
                {item.badge > 0 && (
                  <span
                    className={`absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 text-[9px] font-bold rounded-full flex items-center justify-center text-white bg-red-500 shadow-sm border-2 ${
                      active
                        ? "border-[#ff7a1a]"
                        : "border-white dark:border-[#121214]"
                    }`}
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </div>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNavbar;